import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { AxiosError } from 'axios';
import {
  IReEnrollmentStudentListResponse,
  IReEnrollmentStudentQueryParams,
  IStudentJourneyResponse,
  IStudentJourneyQueryParams,
  ICrossCurriculumTransferPayload,
  ICrossCurriculumTransferResponse,
  // UNIFIED TRANSITION IMPORTS (CVA-3257)
  IUnifiedTransitionPayload,
  IUnifiedTransitionResponse,
} from '../../interface/reEnrollment-student.interface';
import { IApiErrorResponse, IErrorResponse } from '../../interface/main.interface';
import { reEnrollmentStudentService } from '../../services/reEnrollment-student';
import {
  IInitialEnrollmentPayload,
  IInitialEnrollmentResponse,
} from '../../interface/reEnrollment-student.interface';

// UNIFIED TRANSITION ACTION (CVA-3257)
export const unifiedTransition = createAsyncThunk<
  IUnifiedTransitionResponse,
  { cycleId: string; payload: IUnifiedTransitionPayload },
  { rejectValue: IErrorResponse }
>('reEnrollmentStudent/unified-transition', async ({ cycleId, payload }, { rejectWithValue }) => {
  try {
    const response = await reEnrollmentStudentService.unifiedTransition(cycleId, payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || i18n.t('toast.failed_to_transition_students', 'Failed to transition students.'),
    });
  }
});

// POST: Enroll students initially (first-time enrollment into classrooms)
export const enrollInitialStudents = createAsyncThunk<
  IInitialEnrollmentResponse,
  IInitialEnrollmentPayload,
  { rejectValue: IErrorResponse }
>('reEnrollmentStudent/enroll', async (payload, { rejectWithValue }) => {
  try {
    const response = await reEnrollmentStudentService.enrollInitialStudents(payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || i18n.t('toast.failed_to_enroll_students', 'Failed to enroll students.'),
    });
  }
});

// Fetch re-enrollment students for a specific cycle
export const fetchReEnrollmentStudents = createAsyncThunk<
  IReEnrollmentStudentListResponse,
  { cycleId: string; params?: IReEnrollmentStudentQueryParams },
  { rejectValue: IErrorResponse }
>('reEnrollmentStudent/fetch-all', async ({ cycleId, params }, { rejectWithValue }) => {
  try {
    const response = await reEnrollmentStudentService.fetchReEnrollmentStudents(cycleId, params);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || i18n.t('toast.failed_to_fetch_re_enrollment_students', 'Failed to fetch re-enrollment students.'),
    });
  }
});

// Fetch student journey logs
export const fetchStudentJourney = createAsyncThunk<
  IStudentJourneyResponse,
  { studentId: string; params?: IStudentJourneyQueryParams },
  { rejectValue: IErrorResponse }
>('reEnrollmentStudent/fetch-journey', async ({ studentId, params }, { rejectWithValue }) => {
  try {
    const response = await reEnrollmentStudentService.fetchStudentJourney(studentId, params);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || i18n.t('toast.failed_to_fetch_student_journey_logs', 'Failed to fetch student journey logs.'),
    });
  }
});

// PATCH: Cross-Curriculum Transfer
export const crossCurriculumTransfer = createAsyncThunk<
  ICrossCurriculumTransferResponse,
  ICrossCurriculumTransferPayload,
  { rejectValue: IErrorResponse }
>(
  'reEnrollmentStudent/cross-curriculum-transfer',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await reEnrollmentStudentService.crossCurriculumTransfer(payload);
      return response.data;
    } catch (err) {
      const error = err as AxiosError<IApiErrorResponse>;
      const errorData = error.response?.data || (error as IApiErrorResponse);
      return rejectWithValue({
        status: error.response?.status || errorData?.statusCode || 500,
        message: Array.isArray(errorData?.message)
          ? errorData.message.join(', ')
          : errorData?.message || i18n.t('toast.failed_to_transfer_students_across_curricula', 'Failed to transfer students across curricula.'),
      });
    }
  },
);

export const reEnrollmentStudentActions = {
  fetchReEnrollmentStudents,
  fetchStudentJourney,
  enrollInitialStudents,
  crossCurriculumTransfer,
  // UNIFIED TRANSITION ACTION (CVA-3257)
  unifiedTransition,
};
